/**
 * Repositorio de proyectos (doc. 02 §4). La interfaz no sabe si debajo hay IndexedDB o, en la
 * ola 3, un servidor. En la v1 los datos viven solo en este navegador y este equipo.
 */

import Dexie, { type Table } from 'dexie';
import { ulid } from 'ulid';
import type { Proyecto } from '../nucleo/dominio/espacio';
import {
  ESPACIO_DE_TRABAJO_PROPIO,
  espacioDeTrabajoPropio,
  puedeLlevarse,
  type Carpeta,
  type EspacioDeTrabajo,
} from '../nucleo/dominio/organizacion';
import { validarDocumento } from './archivo';
import { esquemaCarpeta, esquemaEspacioDeTrabajo } from './esquema';
import { plantaResumida, type PlantaResumida } from './planta';
import {
  bytesDeTexto,
  comprimir,
  descomprimir,
  huellaDeContenido,
  resumenDeDocumento,
  tamanoDeInstantanea,
  tocaVersionPorTiempo,
  versionesQueSobran,
  type Instantanea,
  type MotivoVersion,
  type VersionGuardada,
} from './versiones';

export interface ResumenProyecto {
  id: string;
  nombre: string;
  descripcion: string;
  origen: string;
  espacioDeTrabajoId: string;
  carpetaId: string | null;
  actualizadoEn: string;
  creadoEn: string;
  espacios: number;
  elementos: number;
  /** Para la miniatura de planta de la tarjeta del proyecto. */
  planta: PlantaResumida;
}

export interface RepositorioProyectos {
  listar(espacioDeTrabajoId?: string): Promise<ResumenProyecto[]>;
  obtener(id: string): Promise<Proyecto | null>;
  guardar(proyecto: Proyecto): Promise<void>;
  eliminar(id: string): Promise<void>;
}

interface FilaProyecto {
  id: string;
  espacioDeTrabajoId: string;
  carpetaId?: string;
  actualizadoEn: string;
  documento: Proyecto;
}

interface FilaVersion extends VersionGuardada, Instantanea {}

class BaseGemba extends Dexie {
  proyectos!: Table<FilaProyecto, string>;
  versiones!: Table<FilaVersion, string>;
  espaciosDeTrabajo!: Table<EspacioDeTrabajo, string>;
  carpetas!: Table<Carpeta, string>;
  preferencias!: Table<{ clave: string; valor: unknown }, string>;

  constructor(nombre: string) {
    super(nombre);
    this.version(1).stores({
      proyectos: 'id, espacioDeTrabajoId, actualizadoEn',
      preferencias: 'clave',
    });
    // Paso 1.5: historial de versiones, espacios de trabajo y carpetas. Una versión anterior de la
    // aplicación abre esta base igual (Dexie la abre en la versión instalada) y no toca lo nuevo.
    this.version(2).stores({
      proyectos: 'id, espacioDeTrabajoId, carpetaId, actualizadoEn',
      versiones: 'id, proyectoId, [proyectoId+numero]',
      espaciosDeTrabajo: 'id',
      carpetas: 'id, espacioDeTrabajoId',
      preferencias: 'clave',
    });
  }
}

export class ErrorDeAlmacenamiento extends Error {}

function resumen(p: Proyecto): ResumenProyecto {
  return {
    id: p.id,
    nombre: p.nombre,
    descripcion: p.descripcion,
    origen: p.origen,
    espacioDeTrabajoId: p.espacioDeTrabajoId,
    carpetaId: p.carpetaId ?? null,
    actualizadoEn: p.actualizadoEn,
    creadoEn: p.creadoEn,
    espacios: p.espacios.length,
    elementos: p.espacios.reduce((a, e) => a + e.layout.elementos.length, 0),
    planta: plantaResumida(p),
  };
}

/** Lo que el repositorio rechaza por una regla de negocio (el mensaje lo arma la interfaz). */
export class ReglaIncumplida extends Error {
  constructor(
    readonly motivo: 'datos-reales' | 'espacio-con-proyectos' | 'espacio-propio' | 'no-existe',
  ) {
    super(motivo);
  }
}

const mensaje = (e: unknown) => (e instanceof Error ? e.message : String(e));

export interface DatosDeVersion {
  motivo: MotivoVersion;
  creadoEn: string;
  versionApp: string;
  detalle?: string;
  nombre?: string;
}

export interface ProyectoAbierto {
  proyecto: Proyecto;
  /** Esquema desde el que se convirtió al abrir (`null` si ya estaba al día). */
  migradoDesde: number | null;
}

export interface UsoDelAlmacenamiento {
  /** Bytes de los proyectos guardados (su documento en JSON). */
  proyectos: number;
  /** Bytes de las instantáneas del historial de versiones. */
  versiones: number;
  cantidadDeVersiones: number;
}

const sinDatos = ({ datos: _d, comprimida: _c, ...v }: FilaVersion): VersionGuardada => v;

export class RepositorioIndexedDB implements RepositorioProyectos {
  private readonly db: BaseGemba;

  constructor(nombreBase = 'gemba-studio') {
    this.db = new BaseGemba(nombreBase);
  }

  async listar(espacioDeTrabajoId?: string): Promise<ResumenProyecto[]> {
    const filas = espacioDeTrabajoId
      ? await this.db.proyectos.where('espacioDeTrabajoId').equals(espacioDeTrabajoId).toArray()
      : await this.db.proyectos.toArray();
    return filas
      .map((f) => resumen(f.documento))
      .sort((a, b) => b.actualizadoEn.localeCompare(a.actualizadoEn));
  }

  async obtener(id: string): Promise<Proyecto | null> {
    const fila = await this.db.proyectos.get(id);
    if (!fila) return null;
    const v = validarDocumento(fila.documento);
    if (!v.ok) throw new ErrorDeAlmacenamiento(`${v.mensaje} ${v.detalles.join(' · ')}`.trim());
    return v.proyecto;
  }

  /**
   * Abre un proyecto para editarlo. Si estaba guardado con un esquema anterior (doc. 03 §5), se
   * convierte, se guarda una versión «migración» con el documento original tal como estaba y se
   * escribe el convertido: abrirlo otra vez ya no lo convierte de nuevo.
   */
  async abrir(id: string, versionApp: string, ahora: string): Promise<ProyectoAbierto | null> {
    const fila = await this.db.proyectos.get(id);
    if (!fila) return null;
    const v = validarDocumento(fila.documento);
    if (!v.ok) throw new ErrorDeAlmacenamiento(`${v.mensaje} ${v.detalles.join(' · ')}`.trim());
    if (v.migradoDesde !== null) {
      await this.guardarVersion(id, fila.documento, {
        motivo: 'migracion',
        detalle: String(v.migradoDesde),
        creadoEn: ahora,
        versionApp,
      });
      await this.guardar(v.proyecto);
    }
    return { proyecto: v.proyecto, migradoDesde: v.migradoDesde };
  }

  async guardar(proyecto: Proyecto): Promise<void> {
    try {
      const fila: FilaProyecto = {
        id: proyecto.id,
        espacioDeTrabajoId: proyecto.espacioDeTrabajoId,
        actualizadoEn: proyecto.actualizadoEn,
        documento: structuredClone(proyecto),
      };
      if (proyecto.carpetaId) fila.carpetaId = proyecto.carpetaId;
      await this.db.proyectos.put(fila);
    } catch (e) {
      throw new ErrorDeAlmacenamiento(`No se pudo guardar en este navegador: ${mensaje(e)}`);
    }
  }

  /**
   * Guarda solo si nadie más lo guardó desde que el editor lo leyó o lo guardó por última vez
   * (`base` es ese `actualizadoEn`): así una pestaña no pisa en silencio lo que guardó otra. Conserva
   * el espacio de trabajo y la carpeta guardados (se cambian desde el inicio, no desde el editor).
   */
  async guardarSiNoCambio(
    proyecto: Proyecto,
    base: string,
  ): Promise<{ ok: true; proyecto: Proyecto } | { ok: false }> {
    try {
      return await this.db.transaction('rw', this.db.proyectos, async () => {
        const fila = await this.db.proyectos.get(proyecto.id);
        if (fila && fila.actualizadoEn !== base) return { ok: false as const };
        const { carpetaId: _c, ...contenido } = proyecto;
        const guardado: Proyecto = fila
          ? {
              ...contenido,
              espacioDeTrabajoId: fila.espacioDeTrabajoId,
              ...(fila.documento.carpetaId ? { carpetaId: fila.documento.carpetaId } : {}),
            }
          : proyecto;
        const nueva: FilaProyecto = {
          id: guardado.id,
          espacioDeTrabajoId: guardado.espacioDeTrabajoId,
          actualizadoEn: guardado.actualizadoEn,
          documento: structuredClone(guardado),
        };
        if (guardado.carpetaId) nueva.carpetaId = guardado.carpetaId;
        await this.db.proyectos.put(nueva);
        return { ok: true as const, proyecto: guardado };
      });
    } catch (e) {
      throw new ErrorDeAlmacenamiento(`No se pudo guardar en este navegador: ${mensaje(e)}`);
    }
  }

  /** Elimina el proyecto y todo su historial de versiones. */
  async eliminar(id: string): Promise<void> {
    await this.db.transaction('rw', this.db.proyectos, this.db.versiones, async () => {
      await this.db.proyectos.delete(id);
      await this.db.versiones.where('proyectoId').equals(id).delete();
    });
  }

  async existe(id: string): Promise<boolean> {
    return (await this.db.proyectos.get(id)) !== undefined;
  }

  // ─── Historial de versiones (paso 1.5) ───────────────────────────────────────────────────────

  /** Versiones del proyecto, de la más nueva a la más antigua (sin las instantáneas). */
  async listarVersiones(proyectoId: string): Promise<VersionGuardada[]> {
    const filas = await this.db.versiones.where('proyectoId').equals(proyectoId).toArray();
    return filas.map(sinDatos).sort((a, b) => b.numero - a.numero);
  }

  async ultimaVersion(proyectoId: string): Promise<VersionGuardada | null> {
    const fila = await this.db.versiones
      .where('[proyectoId+numero]')
      .between([proyectoId, Dexie.minKey], [proyectoId, Dexie.maxKey])
      .last();
    return fila ? sinDatos(fila) : null;
  }

  /**
   * Guarda una instantánea del documento. Después poda las automáticas que sobran (se conservan
   * las últimas 50, todas las manuales y todas las de migración).
   */
  async guardarVersion(
    proyectoId: string,
    documento: unknown,
    datos: DatosDeVersion,
  ): Promise<VersionGuardada> {
    const texto = JSON.stringify(documento);
    const instantanea = await comprimir(texto);
    const d = documento as { versionEsquema?: unknown };
    try {
      return await this.db.transaction('rw', this.db.versiones, async () => {
        const ultima = await this.ultimaVersion(proyectoId);
        const version: VersionGuardada = {
          id: ulid(),
          proyectoId,
          numero: (ultima?.numero ?? 0) + 1,
          creadoEn: datos.creadoEn,
          motivo: datos.motivo,
          versionEsquema: typeof d.versionEsquema === 'number' ? d.versionEsquema : 0,
          versionApp: datos.versionApp,
          huella: huellaDeContenido(documento),
          tamano: tamanoDeInstantanea(instantanea),
          resumen: resumenDeDocumento(documento),
        };
        if (datos.detalle !== undefined) version.detalle = datos.detalle;
        if (datos.nombre) version.nombre = datos.nombre;
        await this.db.versiones.add({ ...version, ...instantanea });
        const todas = await this.db.versiones.where('proyectoId').equals(proyectoId).toArray();
        const sobran = versionesQueSobran(todas);
        if (sobran.length) await this.db.versiones.bulkDelete(sobran);
        return version;
      });
    } catch (e) {
      throw new ErrorDeAlmacenamiento(`No se pudo guardar la versión: ${mensaje(e)}`);
    }
  }

  /**
   * Versión automática: al abrir (si ese contenido no está ya en el historial, por ejemplo después
   * de restaurar una versión) o por tiempo de edición (si cambió desde la última versión y pasaron
   * 10 minutos). Devuelve `null` si no tocaba.
   */
  async versionAutomatica(
    proyecto: Proyecto,
    cuando: 'al-abrir' | 'cada-10-min',
    versionApp: string,
    ahora: string,
  ): Promise<VersionGuardada | null> {
    const huella = huellaDeContenido(proyecto);
    const toca =
      cuando === 'al-abrir'
        ? !(await this.listarVersiones(proyecto.id)).some((v) => v.huella === huella)
        : tocaVersionPorTiempo(await this.ultimaVersion(proyecto.id), huella, ahora);
    if (!toca) return null;
    return this.guardarVersion(proyecto.id, proyecto, {
      motivo: 'automatica',
      detalle: cuando,
      creadoEn: ahora,
      versionApp,
    });
  }

  /** El documento guardado en una versión, tal como se guardó (puede ser de un esquema anterior). */
  async leerVersion(versionId: string): Promise<unknown> {
    const fila = await this.db.versiones.get(versionId);
    if (!fila) throw new ErrorDeAlmacenamiento('Esa versión ya no está en este navegador.');
    return JSON.parse(await descomprimir(fila)) as unknown;
  }

  async eliminarVersion(versionId: string): Promise<void> {
    await this.db.versiones.delete(versionId);
  }

  /** Cuánto ocupan los proyectos y sus versiones (para el medidor de almacenamiento). */
  async uso(): Promise<UsoDelAlmacenamiento> {
    let proyectos = 0;
    await this.db.proyectos.each((f) => {
      proyectos += bytesDeTexto(JSON.stringify(f.documento));
    });
    let versiones = 0;
    let cantidadDeVersiones = 0;
    await this.db.versiones.each((f) => {
      versiones += f.tamano;
      cantidadDeVersiones++;
    });
    return { proyectos, versiones, cantidadDeVersiones };
  }

  // ─── Espacios de trabajo y carpetas (paso 1.5) ──────────────────────────────────────────────

  /** Espacios de trabajo: «Mis proyectos» primero (se crea si falta), luego por nombre. */
  async listarEspaciosDeTrabajo(ahora: string): Promise<EspacioDeTrabajo[]> {
    if (!(await this.db.espaciosDeTrabajo.get(ESPACIO_DE_TRABAJO_PROPIO)))
      await this.db.espaciosDeTrabajo.put(espacioDeTrabajoPropio(ahora));
    const todos = (await this.db.espaciosDeTrabajo.toArray()).flatMap((e) => {
      const v = esquemaEspacioDeTrabajo.safeParse(e);
      return v.success ? [v.data] : [];
    });
    return todos.sort((a, b) =>
      a.id === ESPACIO_DE_TRABAJO_PROPIO
        ? -1
        : b.id === ESPACIO_DE_TRABAJO_PROPIO
          ? 1
          : a.nombre.localeCompare(b.nombre, 'es'),
    );
  }

  async obtenerEspacioDeTrabajo(id: string): Promise<EspacioDeTrabajo | null> {
    const e = await this.db.espaciosDeTrabajo.get(id);
    if (!e) return id === ESPACIO_DE_TRABAJO_PROPIO ? espacioDeTrabajoPropio('') : null;
    const v = esquemaEspacioDeTrabajo.safeParse(e);
    return v.success ? v.data : null;
  }

  async guardarEspacioDeTrabajo(e: EspacioDeTrabajo): Promise<void> {
    await this.db.espaciosDeTrabajo.put(esquemaEspacioDeTrabajo.parse(e));
  }

  /** Solo un espacio de trabajo vacío, y nunca «Mis proyectos». */
  async eliminarEspacioDeTrabajo(id: string): Promise<void> {
    if (id === ESPACIO_DE_TRABAJO_PROPIO) throw new ReglaIncumplida('espacio-propio');
    await this.db.transaction(
      'rw',
      this.db.espaciosDeTrabajo,
      this.db.carpetas,
      this.db.proyectos,
      async () => {
        if ((await this.db.proyectos.where('espacioDeTrabajoId').equals(id).count()) > 0)
          throw new ReglaIncumplida('espacio-con-proyectos');
        await this.db.carpetas.where('espacioDeTrabajoId').equals(id).delete();
        await this.db.espaciosDeTrabajo.delete(id);
      },
    );
  }

  async listarCarpetas(espacioDeTrabajoId: string): Promise<Carpeta[]> {
    const filas = await this.db.carpetas
      .where('espacioDeTrabajoId')
      .equals(espacioDeTrabajoId)
      .toArray();
    return filas
      .flatMap((c) => {
        const v = esquemaCarpeta.safeParse(c);
        return v.success ? [v.data] : [];
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }

  async guardarCarpeta(c: Carpeta): Promise<void> {
    await this.db.carpetas.put(esquemaCarpeta.parse(c));
  }

  /** Elimina la carpeta; sus proyectos quedan «sin carpeta» en el mismo espacio de trabajo. */
  async eliminarCarpeta(id: string): Promise<void> {
    await this.db.transaction('rw', this.db.carpetas, this.db.proyectos, async () => {
      const filas = await this.db.proyectos.where('carpetaId').equals(id).toArray();
      for (const f of filas) {
        const { carpetaId: _c, ...documento } = f.documento;
        const { carpetaId: _f, ...resto } = f;
        await this.db.proyectos.put({ ...resto, documento: documento as Proyecto });
      }
      await this.db.carpetas.delete(id);
    });
  }

  /**
   * Mueve un proyecto a otro espacio de trabajo o carpeta (`null` = sin carpeta). Aplica la regla
   * de integridad 7: un proyecto de un espacio de trabajo con datos reales no pasa a uno sin la
   * marca. La carpeta debe ser del espacio de trabajo de destino.
   */
  async mover(id: string, espacioDeTrabajoId: string, carpetaId: string | null): Promise<void> {
    const fila = await this.db.proyectos.get(id);
    const destino = await this.obtenerEspacioDeTrabajo(espacioDeTrabajoId);
    if (!fila || !destino) throw new ReglaIncumplida('no-existe');
    const origen = await this.obtenerEspacioDeTrabajo(fila.espacioDeTrabajoId);
    const regla = puedeLlevarse(origen?.contieneDatosReales ?? false, destino);
    if (!regla.ok) throw new ReglaIncumplida(regla.motivo);
    if (carpetaId) {
      const c = await this.db.carpetas.get(carpetaId);
      if (!c || c.espacioDeTrabajoId !== espacioDeTrabajoId) throw new ReglaIncumplida('no-existe');
    }
    const { carpetaId: _c, ...documento } = fila.documento;
    await this.guardar({
      ...(documento as Proyecto),
      espacioDeTrabajoId,
      ...(carpetaId ? { carpetaId } : {}),
    });
  }

  /** Copia del proyecto en el mismo lugar, con otro identificador y sin historial. */
  async duplicar(id: string, nuevoId: string, nombre: string, ahora: string): Promise<Proyecto> {
    const original = await this.obtener(id);
    if (!original) throw new ReglaIncumplida('no-existe');
    const copia: Proyecto = {
      ...structuredClone(original),
      id: nuevoId,
      nombre,
      origen: 'duplicado',
      creadoEn: ahora,
      actualizadoEn: ahora,
    };
    await this.guardar(copia);
    return copia;
  }

  cerrar(): void {
    this.db.close();
  }
}
